import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Check, ArrowRight, Landmark, Languages, Wallet, Users, GraduationCap, Globe2,
  FileText, Plane, Home as HomeIcon, MapPin, ClipboardList, Stamp, ShieldCheck, Luggage, Compass, ListChecks, FileCheck,
} from "lucide-react";
import { Reveal, SectionHead, Marquee } from "@/components/site/Widgets";
import { Universities } from "@/pages/Home";
import { AssessmentForm } from "@/components/site/LeadForms";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useMeta } from "@/pages/SeoPages";
import { api, scrollToSection, track, WHATSAPP_URL } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1695314620864-b551fd6574ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxyb21hbmlhJTIwYnVjaGFyZXN0JTIwYXJjaGl0ZWN0dXJlJTIwZXVyb3BlfGVufDB8fHx8MTc4Nzk5MzA5M3ww&ixlib=rb-4.1.0&q=85";

function EligibilityBanner() {
  return (
    <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-2xl border-2 border-[#1E40AF]/20 shadow-xl shadow-blue-900/5 px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h3 className="text-xl sm:text-2xl font-extrabold text-[#0B192C] tracking-tight">Not sure if you qualify for 2026?</h3>
          <p className="text-slate-500 text-sm mt-1">Get a free profile assessment from a Harwell counsellor.</p>
        </div>
        <button onClick={() => scrollToSection("#assessment")} data-testid="romania-eligibility-banner-btn"
          className="shrink-0 px-8 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[0.98] transition-all flex items-center gap-2">
          CHECK MY ELIGIBILITY <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </Reveal>
  );
}

export default function Romania() {
  useMeta("Study in Romania 2026 — Bachelor's & Master's Programs | Harwell Global Education",
    "Study in Romania: English-taught Bachelor's and Master's programs in Medicine, Engineering, Computer Science and Business. Admission process, documents, costs and student life.");
  const [programs, setPrograms] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/content/programs").then((r) => setPrograms(r.data)).catch(() => {});
    api.get("/content/faqs").then((r) => setFaqs(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const whyRomania = [
    { icon: Landmark, title: "European Union Member", desc: "Study within the EU academic environment with degrees built on the European ECTS framework." },
    { icon: Languages, title: "English-Taught Programs", desc: "A growing catalogue of Bachelor's and Master's programs taught entirely in English." },
    { icon: Wallet, title: "Practical Costs", desc: "Tuition and living costs that are often far more manageable than traditional destinations." },
    { icon: GraduationCap, title: "Wide Field Choice", desc: "Medicine, Engineering, Computer Science, Business, Sciences, Humanities and more." },
    { icon: Users, title: "International Community", desc: "Vibrant student cities like Bucharest, Cluj-Napoca, Iași and Timișoara." },
    { icon: Globe2, title: "European Lifestyle", desc: "Live, travel and grow in the heart of Europe while you study." },
  ];

  const popularFields = ["Medicine", "Dentistry", "Engineering", "Computer Science", "Data Science", "Business & Management", "Finance", "International Relations", "Life Sciences", "Architecture"];

  const process = [
    { icon: Compass, title: "Profile Assessment", desc: "We review your academics and goals." },
    { icon: ListChecks, title: "University Selection", desc: "Shortlist programs that fit your profile." },
    { icon: FileCheck, title: "Application", desc: "Prepare and submit your application file." },
    { icon: Stamp, title: "Admission", desc: "Receive your university acceptance." },
    { icon: ShieldCheck, title: "Visa Guidance", desc: "Prepare your student visa application." },
    { icon: Luggage, title: "Pre-Departure", desc: "Travel, accommodation and arrival support." },
  ];

  const documents = [
    "Valid passport",
    "Academic transcripts & certificates (Class 10, 12 and/or degree)",
    "Birth certificate",
    "Proof of English proficiency (where required)",
    "Passport-size photographs",
    "Authenticated / apostilled copies as requested by the university",
    "Medical certificate (for medical programs, where required)",
  ];

  const bachelors = programs.filter((p) => p.degree === "Bachelor's");
  const masters = programs.filter((p) => p.degree === "Master's");

  const ProgramRow = ({ p }) => (
    <div data-testid={`romania-program-${p.id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-xl border border-slate-200 px-6 py-5 hover:border-[#1E40AF]/40 hover:shadow-md transition-all">
      <div>
        <p className="font-bold text-[#0B192C]">{p.name}</p>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
          {p.university} <span className="text-slate-300">•</span> <MapPin className="w-3 h-3" /> {p.location}
        </p>
      </div>
      <div className="text-left sm:text-right shrink-0">
        <p className="text-xs font-data text-slate-500">{p.duration} • {p.language}</p>
        {p.tuition_range && <p className="text-xs font-data text-[#92400E] mt-0.5">{p.tuition_range}</p>}
      </div>
    </div>
  );

  return (
    <>
      {/* HERO */}
      <section data-testid="romania-hero" className="relative mesh-gradient bg-[#F8FAFC] overflow-hidden border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 lg:pt-44 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-white border border-slate-200 shadow-sm rounded-full px-4 py-2 mb-7">
              <span className="text-base leading-none">🇷🇴</span>
              <span className="text-xs font-data uppercase tracking-[0.18em] text-slate-600 font-semibold">Applications Open — 2026 Intake</span>
            </motion.div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0B192C] leading-[1.05]">
              <span className="block overflow-hidden"><motion.span className="block" initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}>STUDY IN</motion.span></span>
              <span className="block overflow-hidden"><motion.span className="block" initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}>ROMANIA <span className="font-editorial italic font-semibold text-[#B45309]">2026</span></motion.span></span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              Bachelor's &amp; Master's Programs. Explore European education with Harwell Global Education.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-9 flex flex-wrap gap-4">
              <button onClick={() => scrollToSection("#assessment")} data-testid="romania-hero-eligibility-btn"
                className="px-8 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-xl shadow-blue-900/20 active:scale-[0.98] transition-all flex items-center gap-2">
                CHECK MY ELIGIBILITY <ArrowRight className="w-4 h-4" />
              </button>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { source: "romania_hero" })}
                data-testid="romania-hero-whatsapp-btn"
                className="px-8 py-4 rounded-full text-sm font-bold text-white bg-[#059669] hover:bg-[#047857] active:scale-[0.98] transition-all">
                WHATSAPP US
              </a>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200 min-h-[320px] lg:min-h-[440px]">
            <img src={HERO_IMG} alt="Bucharest, Romania" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* WHY ROMANIA */}
      <section data-testid="romania-why-section" className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Why Romania" title="EUROPEAN EDUCATION. PRACTICAL COSTS." sub="Romania combines a European academic environment with study costs that make sense for Indian families." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyRomania.map((c, i) => (
              <motion.div key={c.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.09 }} whileHover={{ y: -6 }} data-testid={`romania-why-card-${i}`}
                className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm hover:shadow-xl hover:border-[#1E40AF]/40 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-[#E0F2FE] flex items-center justify-center mb-5">
                  <c.icon className="w-6 h-6 text-[#1E40AF]" />
                </div>
                <h3 className="text-lg font-bold text-[#0B192C]">{c.title}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR FIELDS */}
      <section data-testid="romania-fields-section" className="py-14 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHead overline="Popular Fields" title="WHAT CAN YOU STUDY IN ROMANIA?" />
          <Reveal className="flex flex-wrap justify-center gap-3 -mt-4">
            {popularFields.map((f) => (
              <span key={f} data-testid={`romania-field-chip-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="px-5 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-[#1E40AF] hover:text-[#1E40AF] transition-colors cursor-default">
                {f}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      <div className="py-10"><EligibilityBanner /></div>

      {/* UNIVERSITIES */}
      <div className="py-14"><Universities /></div>

      {/* PROGRAMS */}
      <section data-testid="romania-programs-section" className="py-24 lg:py-28 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Programs" title="BACHELOR'S PROGRAMS" sub="For students completing Class 12." />
          <div className="space-y-4 -mt-6">
            {bachelors.map((p) => <ProgramRow key={p.id} p={p} />)}
          </div>
          <div className="mt-20">
            <SectionHead overline="Programs" title="MASTER'S PROGRAMS" sub="For graduates planning their next academic step." />
            <div className="space-y-4 -mt-6">
              {masters.map((p) => <ProgramRow key={p.id} p={p} />)}
            </div>
          </div>
          <p className="text-center text-xs text-slate-400 mt-10 max-w-2xl mx-auto">
            Program availability, tuition and entry requirements are subject to each university's latest official information.
          </p>
        </div>
      </section>

      <div className="py-10"><EligibilityBanner /></div>

      {/* ADMISSION PROCESS */}
      <section data-testid="romania-process-section" className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Admission Process" title="HOW YOUR APPLICATION MOVES FORWARD" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-6">
            {process.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }} data-testid={`romania-process-step-${i}`} className="text-center lg:text-left">
                <div className="inline-flex w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-sm items-center justify-center mb-5">
                  <s.icon className="w-6 h-6 text-[#1E40AF]" />
                </div>
                <p className="font-data text-xs text-[#B45309] font-semibold tracking-widest">0{i + 1}</p>
                <h3 className="mt-2 text-sm font-extrabold text-[#0B192C] tracking-wide">{s.title}</h3>
                <p className="mt-2 text-xs text-slate-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENTS + COSTS */}
      <section data-testid="romania-docs-costs-section" className="py-24 lg:py-28 bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <Reveal className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <ClipboardList className="w-6 h-6 text-[#1E40AF]" />
              <h3 className="text-xl font-extrabold text-[#0B192C]">REQUIRED DOCUMENTS</h3>
            </div>
            <ul className="space-y-3.5">
              {documents.map((d) => (
                <li key={d} className="flex gap-3 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-[#059669] shrink-0 mt-0.5" /> {d}
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-400 mt-6">Exact requirements vary by university and program — we provide a personalised checklist.</p>
          </Reveal>
          <Reveal delay={0.12} className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <Wallet className="w-6 h-6 text-[#B45309]" />
              <h3 className="text-xl font-extrabold text-[#0B192C]">ESTIMATED COSTS</h3>
            </div>
            <div className="space-y-5">
              <div className="flex justify-between items-center border-b border-slate-100 pb-4">
                <p className="text-sm text-slate-600">Tuition — most Bachelor's / Master's programs</p>
                <p className="text-sm font-data font-semibold text-[#0B192C]">~€2,000 - €4,000 / yr</p>
              </div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-4">
                <p className="text-sm text-slate-600">Tuition — Medicine &amp; Dentistry</p>
                <p className="text-sm font-data font-semibold text-[#0B192C]">~€6,000 - €8,000 / yr</p>
              </div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-4">
                <p className="text-sm text-slate-600">Living costs (city dependent)</p>
                <p className="text-sm font-data font-semibold text-[#0B192C]">~€350 - €500 / mo</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-6 leading-relaxed">
              All figures are indicative estimates to help families plan. Actual tuition and fees are set by each university and are subject to their latest official information.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STUDENT LIFE */}
      <section data-testid="romania-life-section" className="py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200 min-h-[360px]">
            <img src="https://images.unsplash.com/photo-1577985043696-8bd54d9f093f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxzdHVkZW50cyUyMHVuaXZlcnNpdHklMjBjYW1wdXMlMjBldXJvcGV8ZW58MHx8fHwxNzg3OTkzMDkzfDA&ixlib=rb-4.1.0&q=85" alt="Student life on a European campus" className="absolute inset-0 w-full h-full object-cover" />
          </Reveal>
          <div>
            <SectionHead align="left" overline="Student Life" title="LIFE IN ROMANIA'S STUDENT CITIES" />
            <div className="space-y-4 -mt-6">
              {[
                { icon: HomeIcon, text: "University dormitories and private accommodation options across student cities." },
                { icon: Users, text: "International student communities in Bucharest, Cluj-Napoca, Iași and Timișoara." },
                { icon: Plane, text: "European travel, culture and city life at the heart of the continent." },
                { icon: FileText, text: "Harwell's pre-departure guidance prepares you for accommodation, arrival and settling in." },
              ].map((item, i) => (
                <Reveal key={i} delay={i * 0.07} className="flex gap-4 bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <item.icon className="w-5 h-5 text-[#1E40AF] shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="romania-faq-section" className="py-24 lg:py-28 bg-[#F8FAFC] border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Romania FAQ" title="QUESTIONS ABOUT STUDYING IN ROMANIA" />
          <Accordion type="single" collapsible className="space-y-3" data-testid="romania-faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={f.id} value={f.id} data-testid={`romania-faq-item-${i}`}
                className="bg-white border border-slate-200 rounded-xl px-6 data-[state=open]:border-[#1E40AF]/40 data-[state=open]:shadow-lg transition-all">
                <AccordionTrigger className="text-left text-[15px] font-bold text-[#0B192C] py-5 hover:no-underline">{f.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-6">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ASSESSMENT */}
      <section id="assessment" data-testid="romania-assessment-section" className="py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline="Free Profile Assessment" title="CHECK YOUR STUDY ABROAD OPTIONS" sub="Seven quick steps. A counsellor maps your eligible options in Romania and Europe." />
          <Reveal className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5 p-7 sm:p-12">
            <AssessmentForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
